import { CustomErrorBase } from '@mainstage/errors';

type Brand<T, B extends string> = T & { readonly __brand: B };

/** Branded raw string URNs */
export type DomainUrnString = Brand<string, 'DomainUrnString'>;
export type SystemUrnString = Brand<string, 'SystemUrnString'>;
export type ComponentUrnString = Brand<string, 'ComponentUrnString'>;
export type SubcomponentUrnString = Brand<string, 'SubcomponentUrnString'>;

export type AnyUrnString =
  | DomainUrnString
  | SystemUrnString
  | ComponentUrnString
  | SubcomponentUrnString;

export type UrnKind = 'domain' | 'system' | 'component' | 'subcomponent';

export type UrnBase = {
  readonly kind: UrnKind;
  readonly raw: AnyUrnString;

  readonly type: string; // "dp", "cmp", ...
  readonly domain: string; // <domain>
};

export type SystemUrnBase = UrnBase & {
  readonly systemName: string; // <system-name>
  readonly majorVersion: number; // <major version>
};

/**
 * Error thrown when a URN is malformed.
 */
export class MalformedUrn extends CustomErrorBase {
  readonly name = 'MalformedUrn';
}

export type DomainUrn = UrnBase & {
  readonly kind: 'domain';
  readonly raw: DomainUrnString;
};

export type SystemUrn = SystemUrnBase & {
  readonly kind: 'system';
  readonly raw: SystemUrnString;
};

export type ComponentUrn = SystemUrnBase & {
  readonly kind: 'component';
  readonly raw: ComponentUrnString;
  readonly componentName: string;
  /** Parent system URN derived from the component URN. */
  readonly parentSystem: SystemUrn;
};

export type SubcomponentUrn = SystemUrnBase & {
  readonly kind: 'subcomponent';
  readonly raw: SubcomponentUrnString;
  readonly componentName: string;
  readonly subcomponentName: string;
  /** Parent component URN derived from the subcomponent URN. */
  readonly parentComponent: ComponentUrn;
};

export type AnyUrn = DomainUrn | SystemUrn | ComponentUrn | SubcomponentUrn;

const DOMAIN_URN_TYPE = 'dmn';

type ParsedCoreUrn = {
  value: string;
  type: string;
  domain: string;
  systemName?: string;
  majorVersionStr?: string;
  majorVersion?: number;
  componentName?: string;
  subcomponentName?: string;
  partsLength: number;
};

/**
 * Basic structural validation + extraction of core parts from the raw string.
 */
function parseCoreUrn(raw: string): ParsedCoreUrn {
  if (!raw.startsWith('urn:dmb:')) {
    throw new MalformedUrn('URN must start with urn:dmb:');
  }
  const value = raw as AnyUrnString;

  const parts = value.split(':');
  if (parts.length === 4) {
    const [_scheme, _ns, type, domain] = parts;
    if (type !== DOMAIN_URN_TYPE) {
      throw new MalformedUrn(
        `Invalid URN "${value}": expected type "${DOMAIN_URN_TYPE}" for a 4-segment URN`,
      );
    }
    if (!domain) {
      throw new MalformedUrn(`Invalid domain URN "${value}": missing domain`);
    }

    return {
      value,
      type,
      domain,
      partsLength: parts.length,
    };
  }

  if (parts.length < 6 || parts.length > 8) {
    throw new MalformedUrn(
      `Invalid URN "${value}": expected 4 segments for domain URNs or 6 to 8 segments for versioned URNs, got ${parts.length}`,
    );
  }

  const [
    _scheme,
    _ns,
    type,
    domain,
    systemName,
    majorVersionStr,
    componentName,
    subcomponentName,
  ] = parts;

  const majorVersion = Number(majorVersionStr);
  if (!Number.isInteger(majorVersion) || majorVersion < 0) {
    throw new MalformedUrn(
      `Invalid URN "${value}": major version must be a non-negative integer`,
    );
  }

  return {
    value,
    type,
    domain,
    systemName,
    majorVersionStr,
    majorVersion,
    componentName,
    subcomponentName,
    partsLength: parts.length,
  };
}

function buildDomainUrn(core: ParsedCoreUrn): DomainUrn {
  const branded = core.value as DomainUrnString;
  return {
    kind: 'domain',
    raw: branded,
    type: core.type,
    domain: core.domain,
  };
}

function buildSystemUrn(core: ParsedCoreUrn): SystemUrn {
  const branded = core.value as SystemUrnString;
  return {
    kind: 'system',
    raw: branded,
    type: core.type,
    domain: core.domain,
    systemName: core.systemName!,
    majorVersion: core.majorVersion!,
  };
}

function buildParentSystemUrn(core: ParsedCoreUrn): SystemUrn {
  const parentSystemRaw = [
    'urn',
    'dmb',
    'dp',
    core.domain,
    core.systemName,
    core.majorVersionStr,
  ].join(':') as SystemUrnString;

  return {
    kind: 'system',
    raw: parentSystemRaw,
    type: core.type,
    domain: core.domain,
    systemName: core.systemName!,
    majorVersion: core.majorVersion!,
  };
}

function buildComponentUrn(core: ParsedCoreUrn): ComponentUrn {
  if (!core.componentName) {
    throw new MalformedUrn(
      `Invalid component URN "${core.value}": missing component`,
    );
  }

  const branded = core.value as ComponentUrnString;
  const parentSystem = buildParentSystemUrn(core);

  return {
    kind: 'component',
    raw: branded,
    type: core.type,
    domain: core.domain,
    systemName: core.systemName!,
    majorVersion: core.majorVersion!,
    componentName: core.componentName,
    parentSystem,
  };
}

function buildSubcomponentUrn(core: ParsedCoreUrn): SubcomponentUrn {
  if (!core.componentName || !core.subcomponentName) {
    throw new MalformedUrn(
      `Invalid subcomponent URN "${core.value}": missing component or subcomponent`,
    );
  }

  const branded = core.value as SubcomponentUrnString;
  const parentSystem = buildParentSystemUrn(core);

  const parentComponentRaw = [
    'urn',
    'dmb',
    core.type,
    core.domain,
    core.systemName,
    core.majorVersionStr,
    core.componentName,
  ].join(':') as ComponentUrnString;

  const parentComponent: ComponentUrn = {
    kind: 'component',
    raw: parentComponentRaw,
    type: core.type,
    domain: core.domain,
    systemName: core.systemName!,
    majorVersion: core.majorVersion!,
    componentName: core.componentName,
    parentSystem,
  };

  return {
    kind: 'subcomponent',
    raw: branded,
    type: core.type,
    domain: core.domain,
    systemName: core.systemName!,
    majorVersion: core.majorVersion!,
    componentName: core.componentName,
    subcomponentName: core.subcomponentName,
    parentComponent,
  };
}

/**
 * Parses a URN string into its components.
 * Expects a format like:
 *   `urn:dmb:dmn:<domain>`
 * or:
 *   `urn:dmb:<type>:<domain>:<system-name>:<major>[:<component>[:<subcomponent>]]`
 *
 * @throws MalformedUrn if the URN is not valid.
 * @returns The parsed URN object.
 */
export function parseUrn(raw: string): AnyUrn {
  const core = parseCoreUrn(raw);

  switch (core.partsLength) {
    case 4:
      return buildDomainUrn(core);
    case 6:
      return buildSystemUrn(core);
    case 7:
      return buildComponentUrn(core);
    case 8:
      return buildSubcomponentUrn(core);
    default:
      throw new MalformedUrn(`Invalid URN "${core.value}"`);
  }
}

/**
 * Safely parses a URN string into its components.
 *
 * @param raw The raw URN string.
 * @param parseUrnFn The parsing function to use (e.g., parseSystemUrn).
 * @returns The parsed URN object, or undefined if parsing failed.
 */
export function safeParseUrn<T extends UrnBase>(
  raw: string,
  parseUrnFn: (raw: string) => T,
): { success: T; failure: never } | { success: never; failure: Error } {
  try {
    return { success: parseUrnFn(raw), failure: undefined as never };
  } catch (error) {
    return { success: undefined as never, failure: error as Error };
  }
}

// Helpers

export function parseSystemUrn(raw: string): SystemUrn {
  const parsed = parseUrn(raw);
  if (parsed.kind !== 'system') {
    throw new MalformedUrn(
      `Expected a system URN, got a ${parsed.kind} URN: ${raw}`,
    );
  }
  return parsed;
}

export function parseDomainUrn(raw: string): DomainUrn {
  const parsed = parseUrn(raw);
  if (parsed.kind !== 'domain') {
    throw new MalformedUrn(
      `Expected a domain URN, got a ${parsed.kind} URN: ${raw}`,
    );
  }
  return parsed;
}

export function parseComponentUrn(raw: string): ComponentUrn {
  const parsed = parseUrn(raw);
  if (parsed.kind !== 'component') {
    throw new MalformedUrn(
      `Expected a component URN, got a ${parsed.kind} URN: ${raw}`,
    );
  }
  return parsed;
}

export function parseSubcomponentUrn(raw: string): SubcomponentUrn {
  const parsed = parseUrn(raw);
  if (parsed.kind !== 'subcomponent') {
    throw new MalformedUrn(
      `Expected a subcomponent URN, got a ${parsed.kind} URN: ${raw}`,
    );
  }
  return parsed;
}

export function isDomainUrn(urn: AnyUrn): urn is DomainUrn {
  return urn.kind === 'domain';
}

export function isSystemUrn(urn: AnyUrn): urn is SystemUrn {
  return urn.kind === 'system';
}

export function isComponentUrn(urn: AnyUrn): urn is ComponentUrn {
  return urn.kind === 'component';
}

export function isSubcomponentUrn(urn: AnyUrn): urn is SubcomponentUrn {
  return urn.kind === 'subcomponent';
}

/**
 * Returns the parent system URN.
 * If URN is a system URN it returns itself.
 * If URN has no parent system throws error.
 *
 * - component -> parent system
 * - subcomponent -> parent component -> parent system
 * - system -> itself (no parent)
 */
export function getParentSystemUrn(urn: AnyUrn): SystemUrn {
  switch (urn.kind) {
    case 'domain':
      throw new MalformedUrn(
        `URN "${urn.raw}" does not have a parent system URN.`,
      );
    case 'system':
      return urn;
    case 'component':
      return urn.parentSystem;
    case 'subcomponent':
      return urn.parentComponent.parentSystem;
    default:
      throw new MalformedUrn(`URN "${urn}" does not have a parent system URN.`);
  }
}

/**
 * Transforms a URN into a Builder-compatible entity ref.
 * @param urn - the URN to transform
 * @param namespace - optional namespace, defaults to 'default'
 * @returns the Builder-compatible entity ref string
 */
export function transformIntoBuilderEntityRef(
  urn: AnyUrn,
  namespace?: string,
): string {
  const parsedNamespace = namespace ?? 'default';

  switch (urn.kind) {
    case 'domain':
      return `domain:${parsedNamespace}/${urn.domain}`;
    case 'system':
      return `system:${parsedNamespace}/${urn.domain}.${urn.systemName}.${urn.majorVersion}`;
    case 'component':
      return `component:${parsedNamespace}/${urn.domain}.${urn.systemName}.${urn.majorVersion}.${urn.componentName}`;
    case 'subcomponent':
      throw new Error(
        "Subcomponent URNs cannot be transformed to Builder entity refs as they don't have a corresponding entity as of now.",
      );
    default:
      throw new Error(`Unsupported URN kind: ${(urn as any).kind}`);
  }
}

export function safeTransformIntoBuilderEntityRef(
  urn: AnyUrn,
  namespace?: string,
): { success?: string; failure?: Error } {
  try {
    return { success: transformIntoBuilderEntityRef(urn, namespace) };
  } catch (error) {
    return { failure: error as Error };
  }
}
